// scenes/.../video.ts (or wherever your scene objects live)

import SceneObject from "../../../lib/services/scene/sceneobject";
import ShaderService from "../../../lib/services/shader/shader_service";
import ResourceService from "../../../lib/services/resource/resourceservice";
import Camera from "../../../lib/services/scene/camera";
import Stage from "../../../lib/services/stage/stage";
import VideoRenderPipeline from "./video_renderpipeline";
import StickerCompute from "../../../compute/sticker_compute";
import StickerInfoGpu3x3 from "../../../compute/stickerinfogpu3x3";
import DebugTextureRenderPipeline from "./debug_texture_renderpipeline";
import DebugBlobOverlayPipeline from "./debug_blob_overlay_renderpipeline";
import StickerDebug from "./sticker_debug";
import InputService, { Keys} from "../../../lib/services/input/inputservice";
import { StableCodeGate, CodeGateResult } from "./stable_code_gate";
import ScanFeedbackPipeline from "./scan_feedback_renderpipline";
import RenderService from "../../../lib/services/render/render_service";
import CaptureService from "../../../lib/services/capture/capture_service";
import {VideoDisplayInfo,computeVideoRect,sourceUvToDisplayUv} from "./video_display_helper";
import { rb_log, rb_debug_log, rb_throttle } from "../../../lib/services/compute/rb_log";

const MAX_STICKERS = 256;
const STICKER_TYPE_INVALID = 0;
const STICKER_TYPE_STRIP7 = 1;
const STICKER_TYPE_2X2 = 2;
const STICKER_TYPE_3X3 = 3;
const STICKER_TYPE_TARGET  = 4;
const STICKER_TYPE_TRIANGLE =5;
const STICKER_TYPE_CIRCLE = 4;
const STICKER_TYPE_ERROR = 98;
const STICKER_TYPE_DEBUG = 99;


/**
 * Full-screen camera-feed overlay.
 *
 * - Receives ui_message("scan_sticker_bone", ...) to toggle visibility +
 *   start/stop the CaptureService.
 * - When active, each render frame it peeks the latest ColorCameraPool slot
 *   and binds that slot's Y / chroma views into a YUV-to-RGB full-screen
 *   render pipeline.
 * - When inactive, contributes nothing to the render pass.
 *
 * Should be added to the scene LAST so it draws on top of everything else.
 */
export default class Video extends SceneObject {
  private pipeline_nv12: VideoRenderPipeline;
  private pipeline_i420: VideoRenderPipeline;
  private debug_pipeline: DebugTextureRenderPipeline;
  private blob_overlay_nv12: DebugBlobOverlayPipeline;
  private blob_overlay_i420: DebugBlobOverlayPipeline;
  private show_mode: "video" | "mask" | "blob_overlay" = "video";
  private scan_feedback_pipeline:ScanFeedbackPipeline;


  private active_pipeline: VideoRenderPipeline | null = null;
  private active_blob_pipeline: DebugBlobOverlayPipeline | null = null;
  private active: boolean = false;
  private has_bound:boolean = false;
  private sticker_compute:StickerCompute;
  private info:StickerInfoGpu3x3;


  // The pipeline needs to rebuild its bind group whenever the bound texture
  // views change (i.e., after a pool publish that promotes a new slot to
  // Ready). We track which slot views we last bound so we only rebuild on
  // change.
  private bound_slot_id: number = -1;
  private sticker_debug:StickerDebug;

  private code_gate:StableCodeGate;
  render_service:RenderService;
  capture:CaptureService;
  private video_display_info:VideoDisplayInfo;

  // ---- Consumer-side telemetry (logging only) ---------------------------
  // Per-second aggregate counters for the readback consumer path. Reset
  // each reporting interval by log_consumer_rates(). Never hold sticker
  // payloads — only counts, sequences, and codes-as-strings.
  private tel_consumed = 0;        // CPU ready results consumed this interval
  private tel_valid_3x3 = 0;       // frames with >=1 valid 3x3 candidate code
  private tel_zero_code = 0;       // frames with no valid 3x3 candidate code
  private tel_unique_codes = new Set<string>();
  private tel_last: Record<string, unknown> | null = null;

  constructor(key: string) {
    super(key);
    this.pipeline_nv12 = new VideoRenderPipeline("video_nv12", "video_yuv_rgb_NV12");
    this.pipeline_i420 = new VideoRenderPipeline("video_i420", "video_yuv_rgb_I420");
    this.debug_pipeline = new DebugTextureRenderPipeline("debug", "debug_texture");
    this.blob_overlay_nv12 = new DebugBlobOverlayPipeline("blob_nv12", "debug_blob_overlay_nv12");
    this.blob_overlay_i420 = new DebugBlobOverlayPipeline("blob_i420", "debug_blob_overlay_i420");
    this.scan_feedback_pipeline = new ScanFeedbackPipeline("scan_feedback","scan_feedback");
    this.sticker_debug = new StickerDebug();
    this.code_gate = new StableCodeGate(6,10,6);
    this.layer = 1;

    const camera_pool = Stage.instance().capture_service.camera_pool;
    this.sticker_compute = new StickerCompute(camera_pool);
    this.info = new StickerInfoGpu3x3();
    this.render_service = Stage.instance().render_service;
    this.capture = Stage.instance().capture_service;
    this.video_display_info = {
    mode: 0,
    srcW: 0,
    srcH: 0,
    dstW: 0,
    dstH: 0,
  };

  }

  async build(
    device: GPUDevice,
    format: GPUTextureFormat,
    shader_service: ShaderService,
    resource_service: ResourceService
  ): Promise<void> {
     await this.pipeline_nv12.build(device, format, shader_service, resource_service);
      await this.pipeline_i420.build(device, format, shader_service, resource_service);
      await this.debug_pipeline.build(device, format, shader_service, resource_service);
     await this.blob_overlay_nv12.build(device, format, shader_service, resource_service);
     await this.blob_overlay_i420.build(device, format, shader_service, resource_service);
     await this.scan_feedback_pipeline.build(device,format,shader_service,resource_service);
    }

  // ---- Toggle plumbing ---------------------------------------------------

  override ui_message(cmd: string, msg: string): void {
    super.ui_message(cmd, msg);

    if (cmd === "scan_sticker")
    {
      if (this.active) this.deactivate();
      else this.activate();
    }
    else if(cmd === "render")
    {
        if(msg == "color")
        {
           this.show_mode =  "video"; 
        }
        else if(msg == "black and white")
        {
            this.show_mode = "mask";
        }
        else if(msg == "blobs")
        {
            this.show_mode = "blob_overlay";
        }
    }
  }

  private async activate(): Promise<void> {
    const capture = Stage.instance().capture_service;

      this.sticker_compute.cpu_pool.clear();
      this.code_gate.clear();

    try {


      await capture.start();
      
      this.sticker_compute.enable();
      this.active = true;
      this.bound_slot_id = -1;
      rb_debug_log("[video] capture started, overlay active");
    } catch (err) {
      console.error("[video] capture start failed", err);
      this.active = false;
    }
  }

  private deactivate(): void {

      this.sticker_compute.cpu_pool.clear();
      this.code_gate.clear();

      const capture = Stage.instance().capture_service;
      this.sticker_compute.disable();
      capture.stop();
      this.active = false;
      this.has_bound = false;
      this.active_pipeline = null;
      this.bound_slot_id = -1;
      rb_debug_log("[video] capture stopped, overlay hidden");
}
  // ---- Render loop -------------------------------------------------------

  
  private get_orientation_type():
  | "portrait-primary"
  | "portrait-secondary"
  | "landscape-primary"
  | "landscape-secondary"
  | "unknown" {

  const type = window.screen?.orientation?.type;
  if (
    type === "portrait-primary" ||
    type === "portrait-secondary" ||
    type === "landscape-primary" ||
    type === "landscape-secondary"
  ) {
    return type;
  }

  const angle =
    window.screen?.orientation?.angle ??
    (typeof (window as any).orientation === "number"
      ? (window as any).orientation
      : null);

  if (angle === 0) return "portrait-primary";
  if (angle === 180) return "portrait-secondary";
  if (angle === 90) return "landscape-primary";
  if (angle === -90 || angle === 270) return "landscape-secondary";

  return "unknown";
}

private log_orientation_debug(tag = "orient"): void {
  if (typeof window === "undefined") return;

  const so = window.screen?.orientation;
  const vv = window.visualViewport;

  const info = {
    tag,
    ts: new Date().toISOString(),

    screenType: so?.type ?? null,
    screenAngle: so?.angle ?? null,

    windowOrientation:
      typeof (window as any).orientation === "number"
        ? (window as any).orientation
        : null,

    innerWidth: window.innerWidth,
    innerHeight: window.innerHeight,

    clientWidth: document.documentElement?.clientWidth ?? null,
    clientHeight: document.documentElement?.clientHeight ?? null,

    visualViewportWidth: vv?.width ?? null,
    visualViewportHeight: vv?.height ?? null,
    visualViewportScale: vv?.scale ?? null,

    canvasWidth: this.render_service?.canvas?.width ?? null,
    canvasHeight: this.render_service?.canvas?.height ?? null,

    ua: navigator.userAgent,
    platform: navigator.platform,
    maxTouchPoints: navigator.maxTouchPoints ?? 0,
  };

  console.table(info);
}
  update_video_display_info()
  {
    if(!this.render_service.canvas) return;
    const cam = this.capture.camera_info;
     const isAppleMobile = this.detect_ios_handheld_or_tablet();
    const dstW = this.render_service.canvas.width;
    const dstH = this.render_service.canvas.height;
    const orient = this.get_orientation_type();

    //this.log_orientation_debug();

    var mode = 0;
    if (cam.facing === "front") 
    {
       mode =  1; //1
    }
    else if (isAppleMobile) 
    {
      //console.log("isAppleMobile",isAppleMobile,orient)
      if (orient === "portrait-primary") 
      {
        mode = 2;   // rotCW
      } 
      else if (orient === "landscape-primary") 
      {
        mode = 0;   // unrotated
      }
      else if (orient === "landscape-secondary") 
      {
        mode = 3;   // add new shader mode for opposite landscape
      }
      else if (orient === "portrait-secondary") 
      {
        mode = 4;   // add new shader mode for opposite landscape
      }

    }

     //console.log("update_video_display_info",mode,orient)

    this.video_display_info = {
        mode: mode,
        srcW: this.sticker_compute.debug_width,
        srcH: this.sticker_compute.debug_height,
        dstW: dstW,
        dstH: dstH,
    };
  } 
  override tick(delta_time: number): void 
  {
    if(!this.active) return;
    this.update_video_display_info();
    
    const cpu_pool = this.sticker_compute.cpu_pool;
    const slot = cpu_pool.peek_latest_ready_slot();

    if(!slot)
    {
      rb_debug_log("[video tick] null slot" );
    }
    if (!slot || !slot.mapped_sticker_data || !slot.mapped_blob_data) return;
    

      const sticker_bytes = slot.mapped_sticker_data!;
    const sticker_words = new Uint32Array(
      sticker_bytes.buffer,
      sticker_bytes.byteOffset,
      sticker_bytes.byteLength >>> 2
    );

    const blob_bytes = slot.mapped_blob_data!;
    const blob_words = new Uint32Array(
      blob_bytes.buffer,
      blob_bytes.byteOffset,
      blob_bytes.byteLength >>> 2
    );

     const input = Stage.instance().input_service;
    var dump = false;
    if( input.was_key_pressed(Keys.Digit0))
    {
        //dump = true;
        
        // this.sticker_debug.print_blobs(blob_words);
         this.sticker_debug.print_stickers(sticker_words,blob_words);
        
    }
 
    // Telemetry: this is one consumed CPU-ready result.
    const source_seq = slot.source_gpu_sequence;
    this.tel_consumed++;
    let total_parsed = 0;
    let candidates_3x3 = 0;
    let last_pushed_code: bigint = 0n;
    let last_result: CodeGateResult | null = null;
    let accepted = false;

    // Parse the sticker results from the mapped buffer.
    for(var i=0; i< MAX_STICKERS ; i++)
    {

      const xtypex =  this.info.get_sticker_type(i,sticker_words);
      if(xtypex !== STICKER_TYPE_INVALID) total_parsed++;
      if(xtypex == STICKER_TYPE_3X3)
      {
         const code = this.info.get_code(i,sticker_words);
         if(code > 0n)
         {
          candidates_3x3++;
          this.tel_unique_codes.add(code.toString());
          const result = this.code_gate.push(code);
          last_pushed_code = code;
          last_result = result;
         if(result.ok)
         {
            accepted = true;
            // Emit acceptance immediately (unthrottled) BEFORE the pools/gate
            // are cleared, so the accepting frame is always captured.
            rb_log("gate_accept", {
              src_seq: source_seq,
              total_parsed,
              candidates_3x3,
              code: code.toString(),
              accepted_code: result.acceptedCode !== null ? result.acceptedCode.toString() : null,
              reason: result.reason,
              consecutive: result.consecutiveCount,
              window_votes: result.bestWindowVotes,
              window_size: result.windowSize,
            });
            rb_debug_log("3x3 code:", result.acceptedCode, result.reason);

            //i need to clear the buffer
            cpu_pool.clear();
            this.code_gate.clear();

            Stage.instance().footer(code.toString(),1);
            Stage.instance().scene_service.ui_message("sticker",code.toString());
             Stage.instance().scene_service.ui_message("scan_sticker","");

            break;
         }
         else
         {
            //  this.sticker_debug.bad_code(sticker_words,i,blob_words);
         }
        }

      }
     /* else if(xtypex == STICKER_TYPE_2X2)
      {
         const code = this.info.get_code(i,sticker_words);
         if(code > 0n)
         {
            const result = this.code_gate.push(code);
            if(result.ok)
            {
                console.log("2x2 code:", result.acceptedCode, result.reason);

            }
            else
            {
                //  this.sticker_debug.bad_code(sticker_words,i,blob_words);
            }
        }
        
      }
      else if(xtypex == STICKER_TYPE_DEBUG)
      {
         // const code = this.info.get_code(i,sticker_words);
        // const debug_stage = this.info.get_debug_stage(i,sticker_words);
        // this.sticker_debug.log_debug(code,debug_stage ,i);
      }*/
    }

    // Consumer-side telemetry aggregation. Record per-result detail + roll up
    // per-second counters. Acceptance is logged above (immediately); here we
    // only emit throttled samples/rates to avoid per-frame console spam.
    if (candidates_3x3 > 0) this.tel_valid_3x3++;
    else this.tel_zero_code++;

    const gate = this.code_gate.snapshot();
    this.tel_last = {
      src_seq: source_seq,
      total_parsed,
      candidates_3x3,
      code: last_pushed_code.toString(),
      accepted,
      reason: last_result ? last_result.reason : "no_candidate",
      consecutive: gate.consecutiveCount,
      required_consecutive: gate.requiredConsecutive,
      window_votes: gate.bestWindowVotes,
      window_best: gate.bestWindowCode !== null ? gate.bestWindowCode.toString() : null,
      window_len: gate.historyLength,
      window_size: gate.windowSize,
      min_window_votes: gate.minWindowVotes,
    };

    if (rb_throttle("gate_sample")) {
      rb_log("gate_sample", this.tel_last);
    }
    this.log_consumer_rates();
  }

  /**
   * Logging only: once/sec, emit consumer-path aggregate counters, then reset
   * them for the next interval. Bounded unique-code list (cap logged entries).
   */
  private log_consumer_rates(): void {
    if (!rb_throttle("gate_rates")) return;

    const codes = Array.from(this.tel_unique_codes);
    rb_log("gate_rates", {
      consumed: this.tel_consumed,
      valid_3x3: this.tel_valid_3x3,
      zero_code: this.tel_zero_code,
      unique_codes: codes.length,
      codes: codes.slice(0, 8),
    });

    this.tel_consumed = 0;
    this.tel_valid_3x3 = 0;
    this.tel_zero_code = 0;
    this.tel_unique_codes.clear();
  }

  private detect_iphone(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPhone|iPod/.test(ua);
}

private detect_ipad(): boolean {
  if (typeof navigator === "undefined") return false;

  const ua = navigator.userAgent || "";
  const platform = navigator.platform || "";
  const maxTouchPoints = navigator.maxTouchPoints || 0;

  return /iPad/.test(ua) || (platform === "MacIntel" && maxTouchPoints > 1);
}

private detect_ios_handheld_or_tablet(): boolean {
  if (typeof navigator === "undefined") return false;

  const ua = navigator.userAgent || "";
  const platform = navigator.platform || "";
  const maxTouchPoints = navigator.maxTouchPoints || 0;

  const isIPhoneOrIPod = /iPhone|iPod/.test(ua) || /iPhone|iPod/.test(platform);
  const isIPad =
    /iPad/.test(ua) ||
    /iPad/.test(platform) ||
    (platform === "MacIntel" && maxTouchPoints > 1);

  return isIPhoneOrIPod || isIPad;
}

override prepare(device: GPUDevice, camera: Camera): void {
  if (!this.active || !this.render_service || 
    !this.render_service.canvas || !this.sticker_compute.active_gpu_slot) return;
  
 
  this.scan_feedback_pipeline.prepare(device,this.sticker_compute.active_gpu_slot,this.video_display_info);

  if (this.show_mode === "blob_overlay" && this.sticker_compute.debug_blob_view) 
  {
      const capture = Stage.instance().capture_service;
      const cam_slot = capture.camera_pool.peek_latest_ready_slot();
      if (!cam_slot) return;

      const active_blob = cam_slot.format === "I420"
        ? this.blob_overlay_i420
        : this.blob_overlay_nv12;

      active_blob.update_params(
        device, this.video_display_info.mode,
        this.sticker_compute.debug_width,
        this.sticker_compute.debug_height,
        this.video_display_info.dstW,this.video_display_info.dstH
      );
      active_blob.bind_textures(device, cam_slot, this.sticker_compute.debug_blob_view);
      this.active_blob_pipeline = active_blob;
  }


  if (this.show_mode == "mask" && this.sticker_compute.debug_mask_view) 
  {
  
    this.debug_pipeline.update_params(
      device, this.video_display_info.mode,
      this.sticker_compute.debug_width,
      this.sticker_compute.debug_height,
      this.video_display_info.dstW, this.video_display_info.dstH
    );
    this.debug_pipeline.bind_texture(device, this.sticker_compute.debug_mask_view);
    return;
  } 
  const capture = Stage.instance().capture_service;
  const slot = capture.camera_pool.peek_latest_ready_slot();
  if (!slot) return;

  //console.log("[video prepare] cam_slot seq:", slot.sequence, "ref:", (slot as any).__id ?? ((slot as any).__id = Math.random().toString(36).slice(2, 6)));

  // Pick the right pipeline for this slot's format.
  this.active_pipeline =
    slot.format === "I420" ? this.pipeline_i420 : this.pipeline_nv12;

  const canvas = this.render_service.canvas;
  if (!canvas) return;

  

  this.active_pipeline.update_params(
    device,
    this.video_display_info.mode,
    slot.y_width, slot.y_height,
    this.video_display_info.dstW, this.video_display_info.dstH
  );

  this.active_pipeline.bind_slot(device, slot);
  this.active_pipeline.prepare(device);
  this.has_bound = true;
}

override compute(device: GPUDevice, pass: GPUComputePassEncoder, encoder:GPUCommandEncoder): void 
{
  if (!this.active) return;
  this.sticker_compute.dispatch_frame(device,pass, encoder,this.video_display_info);
}

override readback_encode(device: GPUDevice, encoder: GPUCommandEncoder): void
{
    if (!this.active) return;
    
     this.sticker_compute.encode_readback(encoder);
}


override draw(pass: GPURenderPassEncoder): void 
{
    if (!this.active) return;

     switch (this.show_mode)
     {
      case "video":
        if (!this.has_bound || !this.active_pipeline) return;
        this.active_pipeline.draw(pass);
         this.scan_feedback_pipeline.draw(pass);
      break;
    case "mask":
      this.debug_pipeline.draw(pass);
       this.scan_feedback_pipeline.draw(pass);
      break;
    case "blob_overlay":
      if (this.active_blob_pipeline) 
      {
         this.scan_feedback_pipeline.draw(pass);
        this.active_blob_pipeline.draw(pass);
      }
      else if (this.has_bound && this.active_pipeline) 
      {
        this.active_pipeline.draw(pass);
         this.scan_feedback_pipeline.draw(pass);
      }
      break;
    }

   
    
}

 override readback_poll(device: GPUDevice): void
 {
    if (!this.active) return;
    this.sticker_compute.after_submit(device);
 }


}